import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clientConfig } from '../config/clientConfig';

const TRUSTPILOT_LOCALES: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
};

type TrustpilotWidgetProps = {
  size?: 'small' | 'medium' | 'large';
};

// Template ID and height mapping for different sizes
const TEMPLATE_CONFIG = {
  small: {
    templateId: '5419b6ffb0d04a076446a9af',
    height: '20px'
  },
  medium: {
    templateId: '5419b732fbfb950b10de65e5',
    height: '24px'
  },
  large: {
    templateId: '539ad0ffdec7e10e686debd7',
    height: '350px'
  }
} as const;

export default function TrustpilotWidget({ size = 'small' }: TrustpilotWidgetProps) {
  const { i18n } = useTranslation();
  const widgetRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const config = TEMPLATE_CONFIG[size];
  const trustpilotLocale = TRUSTPILOT_LOCALES[i18n.language] ?? TRUSTPILOT_LOCALES.en;

  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 10;
    const retryDelay = 500;

    const tryLoadWidget = () => {
      if (!widgetRef.current) return;

      // Check if Trustpilot is available
      if (window.Trustpilot && window.Trustpilot.loadFromElement) {
        try {
          // Only load if not already loaded
          if (!widgetRef.current.getAttribute('data-tp-loaded')) {
            widgetRef.current.setAttribute('data-tp-loaded', 'true');
            window.Trustpilot.loadFromElement(widgetRef.current);
            setIsLoaded(true);
          }
        } catch (error) {
          console.warn('Error loading Trustpilot widget:', error);
        }
      } else if (retryCount < maxRetries) {
        // Retry if Trustpilot is not ready yet
        retryCount++;
        setTimeout(tryLoadWidget, retryDelay);
      } else {
        console.warn('Trustpilot script not loaded after maximum retries');
      }
    };

    // Start loading process
    tryLoadWidget();

    // Also listen for script load events
    const handleScriptLoad = () => {
      if (!isLoaded) {
        tryLoadWidget();
      }
    };

    // Listen for custom event that indicates script is loaded
    window.addEventListener('trustpilot-loaded', handleScriptLoad);
    
    return () => {
      window.removeEventListener('trustpilot-loaded', handleScriptLoad);
    };
  }, [isLoaded]);

  return (
    <div 
      ref={widgetRef}
      className="trustpilot-widget" 
      data-locale={trustpilotLocale} 
      data-template-id={config.templateId} 
      data-businessunit-id={clientConfig.trustpilotBusinessUnitId}
      data-style-height={config.height}
      data-stars="4,5"
      data-style-width="100%" 
      style={{ minHeight: config.height }}
    >
      <a 
        href={`https://www.trustpilot.com/review/${clientConfig.trustpilotDomain}`}
        target="_blank" 
        rel="noopener"
        style={{ 
          display: isLoaded ? 'none' : 'inline',
          color: '#00b67a',
          textDecoration: 'none',
          fontSize: '14px'
        }}
      >
        ⭐⭐⭐⭐⭐ Trustpilot
      </a>
    </div>
  );
}